<template>
  <div
    v-show="pdf"
    class="pdf-viewer"
    ref="pdfContainer"
    tabindex="0"
    @keydown.stop="handleKeydown"
    :class="{
      'fullscreen-container': isFullscreen,
      'pdf-container-base': true,
      'pdf-container-continuous': continuous
    }"
  >
    <div class="fullscreen flex justify-content-between">
      <div class="flex gap-2">
        <a-tooltip v-if="fullscreenEnabled">
          <template slot="title"> Fullscreen </template>
          <a-button
            class="pdf-action-button flex justify-content-center align-items-center"
            @click="toggleFullscreen"
            :type="isFullscreen ? 'primary' : 'default'"
          >
            <a-icon
              width="20"
              height="20"
              :icon="isFullscreen ? 'material-symbols:close-fullscreen' : 'material-symbols:fullscreen'"
            />
          </a-button>
        </a-tooltip>

        <!-- ✅ ZOOM IN -->
        <a-tooltip>
          <template slot="title"> Zoom In (Ctrl/Cmd +) </template>
          <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="zoomIn" type="default">
            <a-icon width="20" height="20" icon="material-symbols:add" />
          </a-button>
        </a-tooltip>

        <!-- ✅ ZOOM OUT -->
        <a-tooltip>
          <template slot="title"> Zoom Out (Ctrl/Cmd -) </template>
          <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="zoomOut" type="default">
            <a-icon width="20" height="20" icon="material-symbols:remove" />
          </a-button>
        </a-tooltip>

        <!-- ✅ RESET ZOOM -->
        <a-tooltip>
          <template slot="title"> Reset Zoom (Ctrl/Cmd 0) </template>
          <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="resetZoom" type="default">
            <a-icon width="20" height="20" icon="material-symbols:refresh" />
          </a-button>
        </a-tooltip>
      </div>
    </div>
    <div v-for="(page, index) in pages" :key="index" class="pdf-page">
      <canvas :ref="'page_' + (index + 1)" :style="{ width: '100%' }"></canvas>
    </div>
  </div>
</template>

<script>
import { notification } from 'ant-design-vue'
import { PDFDocument } from 'pdf-lib'
const pdfjsLib = require('pdfjs-dist')
pdfjsLib.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.entry.js')

export default {
  name: 'PdfViewer',
  props: {
    file: {
      type: String,
      default: undefined
    },
    fullscreenEnabled: {
      type: Boolean,
      default: true
    },
    continuous: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pages: [],
      pdf: null,
      pdfBytes: null, // ✅ Инициализируем явно
      resizeObserver: null,
      resizeTimeout: null, // Для debounce
      isFullscreen: false,
      renderSessionId: 0,
      renderQueue: Promise.resolve(),
      activeRenderTasks: {},

      zoomLevel: 1.0,
      zoomStep: 0.1,
      minZoom: 0.5,
      maxZoom: 3.0
    }
  },
  watch: {
    file: {
      async handler(newVal) {
        if (newVal) {
          try {
            const response = await fetch(newVal)
            const blob = await response.blob()
            const file = new File([blob], 'document.pdf', {
              type: 'application/pdf'
            })
            await this.loadPdf(file)
          } catch (e) {
            this.showNotification('error', 'Open Error', 'Failed to open file. It may be unavailable or removed.')
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    handleContainerResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        if (this.pdf) {
          this.requestRenderAllPages()
        }
      }, 150)
    },
    zoomIn() {
      if (this.zoomLevel < this.maxZoom) {
        this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom)
        this.requestRenderAllPages()
      }
    },
    zoomOut() {
      if (this.zoomLevel > this.minZoom) {
        this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom)
        this.requestRenderAllPages()
      }
    },
    resetZoom() {
      this.zoomLevel = 1.0
      this.requestRenderAllPages()
    },
    requestRenderAllPages() {
      this.renderQueue = this.renderQueue
        .then(() => this.renderAllPages())
        .catch(error => {
          console.error('PDF render queue error', error)
        })
      return this.renderQueue
    },
    cancelAllRenderTasks() {
      Object.values(this.activeRenderTasks).forEach(task => {
        if (task?.cancel) task.cancel()
      })
      this.activeRenderTasks = {}
    },
    async loadPdfDocument() {
      if (!this.pdfBytes) return null
      if (this.pdf?.destroy) this.pdf.destroy()
      this.pdf = await pdfjsLib.getDocument(this.pdfBytes).promise
      return this.pdf
    },
    async loadPdf(file) {
      const reader = new FileReader()
      reader.onload = async e => {
        const pdfData = new Uint8Array(e.target.result)

        // Если нужна модификация через pdf-lib, оставляем, иначе можно рендерить сразу
        try {
          const pdfDoc = await PDFDocument.load(pdfData)
          this.pdfBytes = await pdfDoc.save()
        } catch (e) {
          console.warn('PDF-lib processing skipped, using original', e)
          this.pdfBytes = pdfData
        }

        await this.$nextTick()
        await this.requestRenderAllPages()
      }
      reader.readAsArrayBuffer(file)
    },
    async renderAllPages() {
      if (!this.pdfBytes) return

      const currentSessionId = ++this.renderSessionId
      this.cancelAllRenderTasks()
      this.pdf = await this.loadPdfDocument()
      if (!this.pdf || currentSessionId !== this.renderSessionId) return
      this.pages = Array.from({ length: this.pdf.numPages }, (_, i) => i + 1)

      await this.$nextTick()

      for (const pageNumber of this.pages) {
        if (currentSessionId !== this.renderSessionId) return
        const page = await this.pdf.getPage(pageNumber)

        const canvasArray = this.$refs[`page_${pageNumber}`]
        const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray

        if (!canvas) continue

        const outputScale = 1

        const container = this.$refs.pdfContainer
        const containerWidth = container?.clientWidth || page.getViewport({ scale: outputScale }).width
        const pageWidth = page.getViewport({ scale: outputScale }).width
        const baseScale = containerWidth / pageWidth
        const scale = baseScale * this.zoomLevel

        const viewport = page.getViewport({ scale })

        canvas.width = Math.round(viewport.width * outputScale)
        canvas.height = Math.round(viewport.height * outputScale)
        canvas.style.width = `${Math.round(viewport.width)}px`
        canvas.style.height = `${Math.round(viewport.height)}px`

        const context = canvas.getContext('2d')
        context.scale(outputScale, outputScale)

        const existingTask = this.activeRenderTasks[pageNumber]
        if (existingTask?.cancel) existingTask.cancel()

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport
        })
        this.activeRenderTasks[pageNumber] = renderTask

        try {
          await renderTask.promise
        } catch (error) {
          if (error?.name !== 'RenderingCancelledException') throw error
        } finally {
          if (this.activeRenderTasks[pageNumber] === renderTask) {
            delete this.activeRenderTasks[pageNumber]
          }
        }
      }
    },
    toggleFullscreen() {
      this.isFullscreen ? this.exitFullscreen() : this.enterFullscreen()
    },
    enterFullscreen() {
      const elem = this.$refs.pdfContainer
      if (!elem) return

      const requestFullscreen =
        elem.requestFullscreen || elem.mozRequestFullScreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen

      if (requestFullscreen) {
        requestFullscreen.call(elem)
        this.isFullscreen = true
        document.addEventListener('fullscreenchange', this.handleFullscreenChange)
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
        document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
        document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
      }
    },
    exitFullscreen() {
      const exitFullscreen =
        document.exitFullscreen ||
        document.mozCancelFullScreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen
      if (exitFullscreen) {
        exitFullscreen.call(document)
      }
    },
    handleFullscreenChange() {
      this.isFullscreen =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement ||
        !!document.msFullscreenElement
      this.$nextTick(() => {
        if (this.pdf) {
          this.requestRenderAllPages()
        }
      })
    },
    scrollDocumentByKeyboard(code) {
      const container = this.$refs.pdfContainer
      if (!container) return
      const step = 50
      if (code === 'ArrowUp') container.scrollTop -= step
      if (code === 'ArrowDown') container.scrollTop += step
      if (code === 'ArrowLeft') container.scrollLeft -= step
      if (code === 'ArrowRight') container.scrollLeft += step
    },
    handleKeydown(event) {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const ctrlKey = isMac ? event.metaKey : event.ctrlKey

      // ✅ Ctrl/Cmd + 0 (Reset Zoom)
      if (ctrlKey && (event.code === 'Digit0' || event.code === 'Numpad0')) {
        event.preventDefault()
        this.resetZoom()
        return
      }

      // ✅ Ctrl/Cmd + + (Zoom In)
      if (ctrlKey && (event.code === 'Equal' || event.code === 'NumpadAdd')) {
        event.preventDefault()
        this.zoomIn()
        return
      }

      // ✅ Ctrl/Cmd + - (Zoom Out)
      if (ctrlKey && (event.code === 'Minus' || event.code === 'NumpadSubtract')) {
        event.preventDefault()
        this.zoomOut()
        return
      }

      // ✅ Стрелки для скролла
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
        // ✅ Не блокируем стандартный скролл браузера, если не нужно,
        // но если хотим свой - preventDefault + stopPropagation
        event.preventDefault()
        this.scrollDocumentByKeyboard(event.code)
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.handleContainerResize)
      if (this.$refs.pdfContainer) {
        this.resizeObserver.observe(this.$refs.pdfContainer)
      }
    } else {
      window.addEventListener('resize', this.handleContainerResize)
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.handleContainerResize)
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    this.cancelAllRenderTasks()
    if (this.pdf?.destroy) this.pdf.destroy()
  }
}
</script>

<style scoped>
/* ✅ Добавим стиль фокуса для доступности */
.pdf-viewer:focus {
  outline: 2px solid var(--primary-color, #3b82f6);
  outline-offset: -2px;
}

canvas {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}

.pdf-container-base {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid var(--border-color-base);
  height: 600px;
  box-sizing: border-box; /* FIX: Важно для стабильности размеров */
  position: relative;
}

.pdf-container-continuous {
  height: auto;
  overflow: visible;
}

.pdf-page {
  position: relative;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.fullscreen {
  width: 100%;
  min-height: 44px;
  padding: 6px 8px;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 100;
  box-sizing: border-box;
  background-color: var(--bg-component);
  border-bottom: 1px solid var(--border-color-base);
  box-shadow: var(--shadow-base);
}

.pdf-action-button.ant-btn-default {
  color: var(--text-color);
  background-color: transparent;
  border-color: transparent;
  box-shadow: none;
}

.pdf-action-button.ant-btn-default:not([disabled]):hover {
  color: var(--text-color);
  background-color: var(--bg-row-hover);
  border-color: var(--border-color-base);
}

.pdf-action-button.ant-btn-default:not([disabled]):focus {
  color: var(--text-color);
  background-color: var(--bg-row-hover);
  border-color: var(--primary-color);
}

.pdf-action-button.ant-btn-default[disabled] {
  color: var(--text-disabled);
  background-color: transparent;
  border-color: transparent;
  opacity: 0.6;
}

.fullscreen-container {
  height: 100vh !important;
  width: 100vw !important;
  overflow: auto;
  position: relative;
}
</style>
